import { useEffect, useRef } from "react";

import { useReducedMotion } from "@/hooks/useReducedMotion";

interface ConfettiProps {
  /** Fires a fresh burst whenever this value changes (and is truthy). */
  fire: boolean;
  /** Number of confetti pieces (default 140). */
  count?: number;
}

interface Piece {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  vrot: number;
  size: number;
  color: string;
  shape: "rect" | "circle";
  life: number;
}

// BCH-brand palette: greens + gold + white.
const COLORS = [
  "#0ac18e",
  "#22c55e",
  "#15803d",
  "#f4c025",
  "#ffffff",
  "#4ade80",
];

/**
 * A one-shot canvas confetti burst rendered in a fixed, non-interactive
 * overlay. Fires whenever `fire` becomes true. Skipped entirely when the user
 * prefers reduced motion.
 */
export function Confetti({ fire, count = 140 }: ConfettiProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const reduced = useReducedMotion();
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!fire || reduced) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const W = window.innerWidth;
    const H = window.innerHeight;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;
    ctx.scale(dpr, dpr);

    // Launch two "cannons" from the lower corners toward the center.
    const pieces: Piece[] = [];
    const origins = [
      { x: W * 0.15, y: H * 0.85, angle: -Math.PI / 3 },
      { x: W * 0.85, y: H * 0.85, angle: (-Math.PI * 2) / 3 },
    ];
    for (let i = 0; i < count; i++) {
      const o = origins[i % origins.length];
      const spread = (Math.random() - 0.5) * 0.9;
      const speed = 9 + Math.random() * 9;
      const angle = o.angle + spread;
      pieces.push({
        x: o.x,
        y: o.y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        rot: Math.random() * Math.PI,
        vrot: (Math.random() - 0.5) * 0.4,
        size: 6 + Math.random() * 6,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        shape: Math.random() > 0.5 ? "rect" : "circle",
        life: 0,
      });
    }

    const gravity = 0.28;
    const drag = 0.99;
    const maxLife = 170;
    let frame = 0;

    const render = () => {
      frame++;
      ctx.clearRect(0, 0, W, H);
      let alive = false;

      for (const p of pieces) {
        p.life++;
        if (p.life > maxLife) continue;
        alive = true;

        p.vx *= drag;
        p.vy = p.vy * drag + gravity;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vrot;

        const fade = Math.max(0, 1 - (p.life - maxLife * 0.6) / (maxLife * 0.4));
        ctx.globalAlpha = Math.min(1, fade);
        ctx.fillStyle = p.color;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        if (p.shape === "rect") {
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        } else {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }
      ctx.globalAlpha = 1;

      if (alive && frame < maxLife + 10) {
        rafRef.current = requestAnimationFrame(render);
      } else {
        ctx.clearRect(0, 0, W, H);
      }
    };

    rafRef.current = requestAnimationFrame(render);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      ctx.clearRect(0, 0, W, H);
    };
  }, [fire, reduced, count]);

  if (reduced) return null;

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none fixed inset-0 z-50"
      aria-hidden
    />
  );
}
